import {
  ArrowDropDown,
  Close,
  Create,
  HelpOutline,
  Minimize,
  NorthEast,
  NorthWest,
  PictureInPictureAlt,
  Reviews,
  SouthEast,
  SouthWest,
  WidthFull
} from '@mui/icons-material';
import {
  Box,
  Fade,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Paper,
  Popover,
  Stack,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import EmptyChat from './EmptyChat';
import ChatList from './ChatList';
import ChatInput from './ChatInput';
import {
  BOTTOM_LEFT,
  BOTTOM_RIGHT,
  positionMode,
  TOP_LEFT,
  TOP_RIGHT
} from '..';
import { useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { setPipPosition, TChatMode } from 'src/features/chatNow';
import { AIAssistantPositionType } from 'src/features/AIAssistantPosition';
import { useNavigate } from 'react-router';
import { CHAT_NOW_GUIDELINES } from 'src/route';

export default function AIAssistantNonSideView({
  isVisible,
  chatInputValue,
  handleChange,
  handleClickTopic,
  handleSendBooleanQueryMessage,
  handleSendChatNowMessage,
  handleSendMessage,
  isLoading,
  sendingLoading,
  handleChangePositionMode,
  handleCloseAIAssistant,
  handleMinimize,
  isMinimized,
  handleClickNewChat
}: {
  isVisible: boolean;
  handleClickTopic: (topic: TChatMode) => void;
  handleSendBooleanQueryMessage: (content: string) => Promise<void>;
  handleSendChatNowMessage: (content: string) => Promise<void>;
  chatInputValue: string;
  isLoading: boolean;
  sendingLoading: boolean;
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSendMessage: (content: string) => Promise<void>;
  handleCloseAIAssistant: () => void;
  handleChangePositionMode: (mode: AIAssistantPositionType) => void;
  handleMinimize: () => void;
  isMinimized: boolean;
  handleClickNewChat: () => Promise<void>;
}) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const handleOpenMode = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMode = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const {
    conversation: chatConversation,
    chatMode,
    booleanConversation,
    pipPosition
  } = useAppSelector((state) => state.chatNow);

  const conversation =
    chatMode === 'chatNow' ? chatConversation : booleanConversation;

  const { position: positionAIAssistant } = useAppSelector(
    (state) => state.storeAIAssistantPosition
  );

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  return (
    <>
      <Fade in={isVisible} timeout={300}>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            right: positionAIAssistant !== 'window' ? 0 : undefined,
            top: positionAIAssistant === 'window' ? pipPosition.y : 0,
            left: positionAIAssistant === 'window' ? pipPosition.x : undefined,
            width: positionAIAssistant === 'window' ? 400 : '100%',
            height: isMinimized
              ? 60
              : positionAIAssistant === 'window'
              ? 500
              : '100%',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: positionAIAssistant === 'window' ? '12px' : '0px',
            overflow: 'hidden',
            border: '1px solid #e0e0e0'
          }}
        >
          <Box
            component="div"
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              padding: 2,
              borderBottom: `1px solid ${theme.palette.grey[300]}`,
              pointerEvents: isLoading || sendingLoading ? 'none' : 'auto'
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Reviews htmlColor={theme.colors.primary.main} />
              <Typography>AI Assistant</Typography>
            </Stack>
            <Stack direction="row" gap={1} alignItems="center">
              <Tooltip
                PopperProps={{
                  style: {
                    zIndex: 9999
                  }
                }}
                title="New Chat"
              >
                <IconButton
                  size="small"
                  sx={{ width: 24, height: 24 }}
                  onClick={handleClickNewChat}
                >
                  <Create fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip
                PopperProps={{
                  style: {
                    zIndex: 9999
                  }
                }}
                title="Guideline"
              >
                <IconButton
                  size="small"
                  onClick={() => navigate(CHAT_NOW_GUIDELINES)}
                  sx={{ width: 24, height: 24 }}
                >
                  <HelpOutline fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip
                PopperProps={{
                  style: {
                    zIndex: 9999
                  }
                }}
                title="Open View Options"
              >
                <Box
                  sx={{
                    cursor: 'pointer',
                    ':hover': {
                      opacity: 0.5
                    },
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    border: 0,
                    backgroundColor: 'transparent'
                  }}
                  onClick={(event) => handleOpenMode(event)}
                  component="button"
                >
                  {positionAIAssistant === 'window' ? (
                    <PictureInPictureAlt fontSize="small" />
                  ) : (
                    <WidthFull fontSize="small" />
                  )}
                  <ArrowDropDown fontSize="small" />
                </Box>
              </Tooltip>
              {positionAIAssistant === 'window' && (
                <Tooltip
                  PopperProps={{
                    style: {
                      zIndex: 9999
                    }
                  }}
                  title="Minimize"
                >
                  <IconButton
                    size="small"
                    onClick={handleMinimize}
                    sx={{ width: 24, height: 24 }}
                  >
                    <Minimize fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip
                PopperProps={{
                  style: {
                    zIndex: 9999
                  }
                }}
                title="Close AI Assistant"
              >
                <IconButton
                  size="small"
                  onClick={handleCloseAIAssistant}
                  sx={{ width: 24, height: 24 }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>

          {/* PiP Content */}
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              alignItems: 'center',
              gap: 1
            }}
          >
            {conversation.length === 0 ? (
              <EmptyChat
                handleClickTopic={handleClickTopic}
                handleSendBooleanQueryMessage={handleSendBooleanQueryMessage}
                handleSendChatNowMessage={handleSendChatNowMessage}
                chatInputValue={chatInputValue}
                handleChange={handleChange}
                handleSendMessage={handleSendMessage}
                isLoading={isLoading}
                sendingLoading={sendingLoading}
              />
            ) : (
              <ChatList
                conversation={conversation}
                sendingLoading={sendingLoading}
              />
            )}

            {chatMode !== null && (
              <ChatInput
                chatInputValue={chatInputValue}
                handleChange={handleChange}
                handleSendMessage={handleSendMessage}
                isLoading={isLoading}
                sendingLoading={sendingLoading}
              />
            )}
          </Box>
          {!isMinimized && positionAIAssistant === 'window' ? (
            <Stack
              direction="row"
              justifyContent="center"
              gap={1}
              paddingBottom={1}
            >
              <IconButton
                sx={{ border: '1px solid #D9D9D9', borderRadius: '10px' }}
                onClick={() => dispatch(setPipPosition(TOP_LEFT))}
                title="Top Left"
              >
                <NorthWest sx={{ fontSize: '20px' }} />
              </IconButton>
              <IconButton
                sx={{ border: '1px solid #D9D9D9', borderRadius: '10px' }}
                onClick={() => dispatch(setPipPosition(TOP_RIGHT))}
                title="Top Right"
              >
                <NorthEast sx={{ fontSize: '20px' }} />
              </IconButton>
              <IconButton
                sx={{ border: '1px solid #D9D9D9', borderRadius: '10px' }}
                onClick={() => dispatch(setPipPosition(BOTTOM_LEFT))}
                title="Bottom Left"
              >
                <SouthWest sx={{ fontSize: '20px' }} />
              </IconButton>
              <IconButton
                sx={{ border: '1px solid #D9D9D9', borderRadius: '10px' }}
                onClick={() => dispatch(setPipPosition(BOTTOM_RIGHT))}
                title="Bottom Right"
              >
                <SouthEast sx={{ fontSize: '20px' }} />
              </IconButton>
            </Stack>
          ) : null}
          <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={handleCloseMode}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center'
            }}
            sx={{
              zIndex: 9999
            }}
          >
            <List>
              {positionMode.map((mode) => (
                <ListItem
                  disablePadding
                  key={mode.value}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <ListItemButton
                    onClick={() => {
                      handleChangePositionMode(mode.value);
                      setAnchorEl(null);
                    }}
                    sx={{
                      '&:hover': {
                        backgroundColor: theme.palette.primary.light
                      },
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%',
                      gap: 1,
                      backgroundColor:
                        positionAIAssistant === mode.value
                          ? theme.palette.primary.light
                          : ''
                    }}
                  >
                    <Typography sx={{ fontSize: '12px' }}>
                      {mode.label}
                    </Typography>
                    {mode.icon}
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Popover>
        </Paper>
      </Fade>
    </>
  );
}
